import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'
import { FrameProps } from '@editor/interfaces/canvas'
import { getCenterOfTwoPoints } from '@editor/services/Editor'
import { findMissingVertexParallelogram, pointsToVector3s } from '@editor/services/Points'
import { StylesProvider, Tooltip } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { uniqueId } from 'lodash'
import { Line } from 'three'

export const Frame = ({
  points,
  color = 'white',
  labelColor,
  needFillMissingPoint = false,
  label,
  errorMessage,
}: FrameProps) => {
  const ref = useRef<Line>(undefined)
  const [tooltipKey, setTooltipKey] = useState(uniqueId('frame-error-tooltip'))
  const pointsV3 = useMemo(() => pointsToVector3s(points), [points])

  if (pointsV3.length === 3 && needFillMissingPoint) {
    pointsV3.push(findMissingVertexParallelogram(pointsV3))
    pointsV3.push(pointsV3[0].clone())
  }

  useFrame(() => {
    if (ref.current) {
      ref.current.geometry.setFromPoints(pointsV3)
      ref.current.computeLineDistances()
    }
  })

  /**
   * Set tooltip key to force update tooltip position
   */
  const onWindowResize = useCallback(() => {
    setTooltipKey(uniqueId('frame-error-tooltip'))
  }, [setTooltipKey])

  /**
   * Add event listener on window resize to update tooltip position
   */
  useEffect(() => {
    window.addEventListener('resize', onWindowResize)

    return () => {
      window.removeEventListener('resize', onWindowResize)
    }
  }, [onWindowResize])

  return (
    <>
      {/* ref must be any */}
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any */}
      <line ref={ref as any}>
        <bufferGeometry />
        <lineDashedMaterial
          transparent
          depthTest={false}
          depthWrite={false}
          color={color}
          dashSize={0.005}
          gapSize={0.005}
        />
        {label && labelColor && (
          <Html
            position={pointsV3.length >= 2 ? getCenterOfTwoPoints(pointsV3[0], pointsV3[1]) : pointsV3[0]}
            style={{ transform: 'translateX(-50%) translateY(-50%)' }}
            zIndexRange={[1, 9]}
          >
            <StylesProvider>
              {errorMessage ? (
                <Tooltip
                  key={tooltipKey}
                  showArrow
                  positioning={{ placement: 'top' }}
                  label={errorMessage}
                  /* TODO: style migration needed - p=8px */
                  /* TODO: style migration needed - fontWeight=normal */
                  /* className="canvas-tooltip" */
                  /* TODO: style migration needed - defaultIsOpen=true */
                >
                  <Box
                    backgroundColor="yellow"
                    py={0}
                    px={4}
                    fontSize="80%"
                    fontWeight="bold"
                    color="black"
                    whiteSpace="nowrap"
                  >
                    ⚠️ {label}
                  </Box>
                </Tooltip>
              ) : (
                <Box
                  backgroundColor={labelColor}
                  py={0}
                  px={4}
                  fontSize="80%"
                  fontWeight="bold"
                  color="white"
                  whiteSpace="nowrap"
                >
                  {label}
                </Box>
              )}
            </StylesProvider>
          </Html>
        )}
      </line>
    </>
  )
}
